/**
 * PKCE (S256) helpers for the native Google sign-in hand-off. The app generates
 * a high-entropy `verifier`, sends only its `challenge` (a SHA-256 hash) to the
 * server, and later proves possession of the verifier when exchanging the
 * authorization code — so a code intercepted off the custom scheme is useless
 * without the verifier, which never leaves the app.
 */
export type PkcePair = { verifier: string; challenge: string };

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export async function generatePkcePair(): Promise<PkcePair> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  const challenge = base64Url(new Uint8Array(digest));
  return { verifier, challenge };
}
